import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useId } from 'react';
import { di } from 'react-magnetic-di';
import { createContainer, createHook, createStore } from 'react-sweet-state';
import { CSM, getCurrentConversation, hide, onClose, onOpen } from '@atlassian/customer-service-management-widget-sdk-react';
var actions = {
  setInputValue: function setInputValue(inputValue) {
    return function (_ref) {
      var setState = _ref.setState;
      setState({
        inputValue: inputValue
      });
    };
  },
  observe: function observe(id, element) {
    return function (_ref2) {
      var setState = _ref2.setState,
        getState = _ref2.getState;
      var _getState = getState(),
        triggers = _getState.triggers,
        currentObserver = _getState.observer;
      var observer = currentObserver !== null && currentObserver !== void 0 ? currentObserver : new IntersectionObserver(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 1),
          entry = _ref4[0];
        setState({
          triggers: _objectSpread(_objectSpread({}, triggers), {}, _defineProperty({}, id, {
            element: element,
            isIntersecting: entry.isIntersecting
          }))
        });
      }, {
        threshold: 0
      });
      setState({
        observer: observer
      });
      observer.observe(element);
    };
  },
  disconnect: function disconnect(id) {
    return function (_ref5) {
      var setState = _ref5.setState,
        getState = _ref5.getState;
      var _getState2 = getState(),
        prevTriggers = _getState2.triggers,
        observer = _getState2.observer;
      if (observer && prevTriggers[id]) {
        observer.unobserve(prevTriggers[id].element);
      }
      var triggers = _objectSpread({}, prevTriggers);
      delete triggers[id];
      setState({
        triggers: triggers
      });
    };
  }
};
var StoreContainer = createContainer();
export var AiSearchTriggerContainer = function AiSearchTriggerContainer(_ref6) {
  var children = _ref6.children,
    props = _objectWithoutProperties(_ref6, _excluded);
  return /*#__PURE__*/React.createElement(StoreContainer, _extends({}, props, {
    isGlobal: true
  }), children);
};
var store = createStore({
  name: 'csm-ai-search-trigger-store',
  initialState: {
    inputValue: '',
    widgetVisibility: null,
    triggers: {},
    observer: null
  },
  actions: actions,
  containedBy: StoreContainer,
  handlers: {
    onInit: function onInit() {
      return function (_ref7, _ref8) {
        var setState = _ref7.setState;
        var siteProp = _ref8.site,
          siteId = _ref8.siteId,
          widgetId = _ref8.widgetId;
        var site = siteProp !== null && siteProp !== void 0 ? siteProp : typeof window !== 'undefined' && window.location.hostname;
        if (!site) {
          return;
        }
        CSM({
          site: site,
          siteId: siteId,
          widgetId: widgetId
        });
        hide();
        setState({
          widgetVisibility: 'hidden'
        });
        onOpen(function () {
          setState({
            widgetVisibility: 'visible'
          });
        });
        onClose(function () {
          getCurrentConversation(function (conversation) {
            if (!conversation) {
              hide();
              setState({
                widgetVisibility: 'hidden'
              });
            }
          });
        });
      };
    }
  }
});
var searchFieldValueSelector = function searchFieldValueSelector(state) {
  return state.inputValue;
};
var useSearchFieldValueHook = createHook(store, {
  selector: searchFieldValueSelector
});

/**
 * Read and write the search field value
 */
export var useAiSearch = function useAiSearch() {
  var _useSearchFieldValueH = useSearchFieldValueHook(),
    _useSearchFieldValueH2 = _slicedToArray(_useSearchFieldValueH, 2),
    inputValue = _useSearchFieldValueH2[0],
    setInputValue = _useSearchFieldValueH2[1].setInputValue;
  return {
    value: inputValue,
    onChange: function onChange(value) {
      return setInputValue(value);
    }
  };
};
var useAiSearchStore = createHook(store);

/**
 * Connect a search trigger to be observed
 */
export var useTriggerIntersectionObserver = function useTriggerIntersectionObserver() {
  var id = useId();
  var _useAiSearchStore = useAiSearchStore(),
    _useAiSearchStore2 = _slicedToArray(_useAiSearchStore, 2),
    _ = _useAiSearchStore2[0],
    _useAiSearchStore2$ = _useAiSearchStore2[1],
    observe = _useAiSearchStore2$.observe,
    disconnect = _useAiSearchStore2$.disconnect;
  var ref = useCallback(function (element) {
    if (element) {
      observe(id, element);
    } else {
      disconnect(id);
    }
  }, [observe, disconnect, id]);
  return {
    ref: ref
  };
};
var hasIntersectingTriggerSelector = function hasIntersectingTriggerSelector(state) {
  return Object.values(state.triggers).some(function (trigger) {
    return trigger.isIntersecting;
  });
};
var useHasIntersectingTriggerHook = createHook(store, {
  selector: hasIntersectingTriggerSelector
});

/**
 * Determine if any tracked trigger is intersecting the viewport
 * @returns true if any tracked trigger is intersecting the viewport
 */
export var useHasIntersectingTrigger = function useHasIntersectingTrigger() {
  var _useHasIntersectingTr = useHasIntersectingTriggerHook(),
    _useHasIntersectingTr2 = _slicedToArray(_useHasIntersectingTr, 1),
    hasIntersectingTrigger = _useHasIntersectingTr2[0];
  return hasIntersectingTrigger;
};

/**
 * Exposes whether the search triggers should be visible or not
 */
export var useSearchTriggerVisibility = function useSearchTriggerVisibility() {
  var _useAiSearchStore3 = useAiSearchStore(),
    _useAiSearchStore4 = _slicedToArray(_useAiSearchStore3, 1),
    widgetVisibility = _useAiSearchStore4[0].widgetVisibility;
  return {
    isTriggerVisible: widgetVisibility !== 'visible'
  };
};